import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  getOrderById,
  updateOrderStatus,
  completeOrder,
  deleteOrder
} from "../services/api";
import Card from "../components/Card";
import {
  formatCurrency,
  formatDateInEastern,
  formatDateInEasternDT,
  formatTimeInEastern,
  formatTimeInEasternTI
} from "../utils/formatters";
import {
  FaArrowLeft,
  FaEdit,
  FaCheck,
  FaTimes,
  FaSpinner,
  FaChevronDown,
  FaChevronRight,
  FaChevronLeft,
  FaTrash,
  FaCheckCircle,
  FaTimesCircle
} from "react-icons/fa";

import { format, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

// Styled components with improved styling
const PageWrapper = styled.div`
  padding: 24px;
  background-color: var(--bg-primary, #1a1a1a);
  min-height: 100vh;
  width: 100%;
`;

const OrderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const OrderTitle = styled.h2`
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--text-primary, #f0f0f0);
  margin: 0;
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  color: var(--primary, #6366f1);
  font-weight: 500;
  transition: all 0.2s;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;

  &:hover {
    color: var(--primary-dark, #4f46e5);
    background-color: var(--hover-color, #2c2c2c);
    text-decoration: none;
  }

  svg {
    margin-right: 0.5rem;
  }
`;

const OrderDetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const DetailItem = styled.div`
  background-color: var(--bg-secondary, #252525);
  padding: 1.25rem;
  border-radius: 8px;
  border-left: 3px solid var(--primary, #6366f1);
`;

const DetailLabel = styled.div`
  font-size: 0.875rem;
  color: var(--text-secondary, #aaa);
  margin-bottom: 0.5rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const DetailValue = styled.div`
  font-weight: 500;
  color: var(--text-primary, #f0f0f0);
  word-break: break-word;
  font-size: 1rem;
`;

const OrderItemsContainer = styled.div`
  background-color: var(--card-bg, #2a2a2a);
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 2rem;
`;

const OrderItemsHeader = styled.div`
  background-color: var(--card-header-bg, #2a2a2a);
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border-color, #3a3a3a);
`;

const OrderItemsList = styled.div`
  display: flex;
  flex-direction: column;
`;

const OrderItemCard = styled.div`
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--border-color, #3a3a3a);
  display: grid;
  grid-template-columns: 2fr 1fr 2fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const OrderItemDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const OrderItemName = styled.div`
  font-weight: 600;
  color: var(--text-primary, #f0f0f0);
  font-size: 1.05rem;
`;

const OrderItemVariant = styled.div`
  font-size: 0.9rem;
  color: var(--text-secondary, #aaa);
`;

const OrderItemDosage = styled.div`
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: var(--text-secondary, #aaa);
  font-style: italic;
  padding-left: 0.5rem;
  border-left: 2px solid var(--primary, #6366f1);
`;

const OrderItemPrice = styled.div`
  font-weight: 600;
  color: var(--primary, #6366f1);
  font-size: 1.125rem;
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding: 1rem;
  border-top: 1px solid var(--border-color, #3a3a3a);
`;

const PageButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid var(--border-color, #3a3a3a);
  background-color: ${(props) =>
    props.$active ? "var(--primary, #6366f1)" : "var(--bg-secondary, #252525)"};
  color: ${(props) =>
    props.$active ? "white" : "var(--text-primary, #f0f0f0)"};

  &:hover:not(:disabled) {
    background-color: ${(props) =>
      props.$active
        ? "var(--primary-dark, #4f46e5)"
        : "var(--hover-color, #2c2c2c)"};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    margin-right: ${(props) => (props.iconRight ? "0" : "0.375rem")};
    margin-left: ${(props) => (props.iconRight ? "0.375rem" : "0")};
  }
`;

const PageInfo = styled.div`
  color: var(--text-secondary, #aaa);
  font-size: 0.875rem;
`;

const OrderSummary = styled.div`
  margin: 2rem 0;
  padding: 1.5rem;
  background-color: var(--bg-secondary, #252525);
  border-radius: 8px;
  border-left: 3px solid var(--primary, #6366f1);
`;

const OrderSummaryHeader = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary, #f0f0f0);
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--border-color, #3a3a3a);
`;

const SummaryTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${(props) => (props.$total ? "0.75rem 0 0" : "0")};
  margin-top: ${(props) => (props.$total ? "0.5rem" : "0")};
  font-size: ${(props) => (props.$total ? "1.125rem" : "0.95rem")};
  font-weight: ${(props) => (props.$total ? "600" : "400")};
  border-top: ${(props) =>
    props.$total ? `1px solid var(--border-color, #3a3a3a)` : "none"};
`;

const SummaryLabel = styled.span`
  color: var(--text-secondary, #aaa);
`;

const SummaryValue = styled.span`
  color: ${(props) =>
    props.$total ? "var(--primary, #6366f1)" : "var(--text-primary, #f0f0f0)"};
  font-weight: ${(props) => (props.$total ? "700" : "inherit")};
  font-size: ${(props) => (props.$total ? "1.25rem" : "inherit")};
`;

const OrderStatus = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.375rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background-color: ${(props) => {
    if (props.status === "to_be_posted") return "rgba(59, 130, 246, 0.1)";
    if (props.status === "in_progress") return "rgba(234, 179, 8, 0.15)";
    if (props.status === "failed") return "rgba(239, 68, 68, 0.1)";
    if (props.status === "completed") return "rgba(16, 185, 129, 0.1)";
    if (props.status === "approved") return "rgba(16, 185, 129, 0.1)";
    return "rgba(234, 179, 8, 0.1)";
  }};
  color: ${(props) => {
    if (props.status === "to_be_posted") return "var(--info, #3b82f6)";
    if (props.status === "in_progress") return "var(--warning, #eab308)";
    if (props.status === "failed") return "var(--error, #ef4444)";
    if (props.status === "completed") return "var(--success, #10b981)";
    if (props.status === "approved") return "var(--success, #10b981)";
    return "var(--warning, #eab308)";
  }};
`;

const AddressCard = styled.div`
  background-color: var(--bg-secondary, #252525);
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border-left: 3px solid var(--primary, #6366f1);
`;

const SectionTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary, #f0f0f0);
  margin: 0 0 1.25rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--border-color, #3a3a3a);
`;

const AddressLine = styled.div`
  color: var(--text-primary, #f0f0f0);
  margin-bottom: 0.375rem;
  font-size: 0.95rem;
`;

const CityStateZip = styled.div`
  color: var(--text-secondary, #aaa);
  font-size: 0.9rem;
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-secondary, #aaa);
  font-size: 1.125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;

  svg {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--error, #ef4444);
  font-size: 1.125rem;
`;

const SuccessMessage = styled.div`
  padding: 12px 16px;
  background-color: rgba(16, 185, 129, 0.1);
  color: #10b981;
  border-radius: 8px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  flex-wrap: wrap;

  @media (max-width: 580px) {
    flex-direction: column;
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-size: 0.95rem;
  padding: 0.75rem 1.25rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    font-size: 1rem;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: var(--primary, #6366f1);
  color: white;

  &:hover:not(:disabled) {
    background-color: var(--primary-dark, #4f46e5);
  }
`;

const DangerButton = styled(Button)`
  background-color: rgba(239, 68, 68, 0.1);
  color: var(--error, #ef4444);

  &:hover:not(:disabled) {
    background-color: rgba(239, 68, 68, 0.2);
  }
`;

const SuccessButton = styled(Button)`
  background-color: rgba(16, 185, 129, 0.1);
  color: var(--success, #10b981);

  &:hover:not(:disabled) {
    background-color: rgba(16, 185, 129, 0.2);
  }
`;

const ApproveButton = styled(Button)`
  background-color: #d1fae5;
  color: #059669;

  &:hover:not(:disabled) {
    background-color: #a7f3d0;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background-color: var(--card-bg, #2a2a2a);
  border-radius: 10px;
  padding: 2rem;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary, #f0f0f0);
  margin: 0;
`;

const ModalBody = styled.div`
  margin-bottom: 2rem;
  color: var(--text-primary, #f0f0f0);
  font-size: 1rem;
  line-height: 1.5;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

const CancelButton = styled(Button)`
  background-color: var(--bg-secondary-button, #3a3a3a);
  color: var(--text-primary, #f0f0f0);

  &:hover:not(:disabled) {
    background-color: var(--bg-secondary-button-hover, #444444);
  }
`;

const NotesSection = styled.div`
  background-color: var(--bg-secondary, #252525);
  border-radius: 8px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border-left: 3px solid var(--primary, #6366f1);

  p {
    color: var(--text-primary, #f0f0f0);
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.5;
  }
`;

// New styled components for pharmacy response modal
const PharmacyResponseContainer = styled.div`
  margin-top: 1rem;
`;

const PharmacyResponseHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  gap: 0.5rem;
`;

const ResponseStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  color: ${(props) =>
    props.success ? "var(--success, #10b981)" : "var(--error, #ef4444)"};
`;

const MedicationList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const MedicationItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  border-bottom: 1px solid var(--border-color, #3a3a3a);

  &:last-child {
    border-bottom: none;
  }
`;

const StatusIcon = styled.div`
  display: flex;
  align-items: center;
  color: ${(props) =>
    props.success ? "var(--success, #10b981)" : "var(--error, #ef4444)"};
`;

const ErrorList = styled.ul`
  list-style: disc;
  padding-left: 1.5rem;
  color: var(--error, #ef4444);
  margin-top: 1rem;
`;

function OrderDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalAction, setModalAction] = useState("");
  const [modalError, setModalError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Delete functionality states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Approval functionality states
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(false);

  // Pharmacy response state
  const [pharmacyResponse, setPharmacyResponse] = useState(null);
  const [showResponseModal, setShowResponseModal] = useState(false);

  // Success message state
  const [successMessage, setSuccessMessage] = useState("");

  // User state
  const [user, setUser] = useState(null);

  // Error handling state for completion errors
  const [completionErrorMessage, setCompletionErrorMessage] = useState("");
  const [showCompletionErrorModal, setShowCompletionErrorModal] = useState(false);

  useEffect(() => {
    // Get user info from localStorage
    const userString = localStorage.getItem("user");
    if (userString) {
      const userObject = JSON.parse(userString);
      setUser(userObject);
    }

    const fetchOrderDetails = async () => {
      try {
        const data = await getOrderById(id);
        console.log("Fetched order details:", data);
        setOrder(data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching order details:", err);
        setError("Error loading order details. Please try again later.");
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [id]);

  const handleApproveOrder = () => {
    setModalAction("completed");
    setShowModal(true);
    setModalError("");
  };

  const handleRejectOrder = () => {
    setModalAction("reject");
    setShowModal(true);
    setModalError("");
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  // Navigate to edit page instead of opening modal
  const handleNavigateToEditPage = () => {
    navigate(`/orders/${id}/edit`);
  };

  const handleConfirmAction = async () => {
    setUpdateLoading(true);
    setModalError("");

    try {
      const newStatus = modalAction === "approve" ? "approved" : "failed";
      const updatedOrder = await updateOrderStatus(id, newStatus);
      setOrder(updatedOrder);
      setShowModal(false);

      // Show success message
      setSuccessMessage(
        `Order has been marked as ${
          modalAction === "approve" ? "approved" : "failed"
        }.`
      );

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage("");
      }, 3000);
    } catch (err) {
      console.error(
        `Error ${modalAction === "approve" ? "approving" : "rejecting"} order:`,
        err
      );
      setModalError(
        `Failed to ${modalAction} order. ${
          err.message || "Please try again later."
        }`
      );
    } finally {
      setUpdateLoading(false);
    }
  };

  // Function to open approval confirmation modal for complete order
  const openApprovalModal = () => {
    setShowApprovalModal(true);
  };

  // Function to close approval confirmation modal
  const closeApprovalModal = () => {
    setShowApprovalModal(false);
  };

  // Function to close completion error modal
  const closeCompletionErrorModal = () => {
    setShowCompletionErrorModal(false);
    setCompletionErrorMessage("");
  };

  // Function to handle order completion
  const handleCompleteOrder = async () => {
    setApprovalLoading(true);
    try {
      const response = await completeOrder(order.order_id);

      // Store the pharmacy response from the API
      if (response.pharmacy_response) {
        setPharmacyResponse(response.pharmacy_response);
        // Close the approval modal and show the response modal
        setShowApprovalModal(false);
        setShowResponseModal(true);
      }

      // Refresh order data
      const refreshedOrder = await getOrderById(id);
      setOrder(refreshedOrder);

      if (!response.pharmacy_response) {
        // If no pharmacy response, just close the approval modal and show success message
        setShowApprovalModal(false);
        setSuccessMessage("Order has been completed successfully.");

        // Clear success message after 3 seconds
        setTimeout(() => {
          setSuccessMessage("");
        }, 3000);
      }
    } catch (err) {
      console.error("Error completing order:", err);
      
      // Handle 409 conflict error specifically
      if (err.status === 409 && err.data) {
        const { message, current_status, order_id } = err.data;
        setCompletionErrorMessage(
          `${message}. Order ${order_id} is currently in "${current_status}" status.`
        );
        setShowApprovalModal(false);
        setShowCompletionErrorModal(true);
        
        // Refresh order data to show updated status
        try {
          const refreshedOrder = await getOrderById(id);
          setOrder(refreshedOrder);
        } catch (refreshErr) {
          console.error("Error refreshing order data:", refreshErr);
        }
      } else if (err.response && err.response.data) {
        // If we have a pharmacy_response in the error data, use it
        if (err.response.data.pharmacy_response) {
          setPharmacyResponse(err.response.data.pharmacy_response);
          setShowApprovalModal(false);
          setShowResponseModal(true);
        } else {
          // Show generic error in modal
          setCompletionErrorMessage(err.message || "Failed to complete order. Please try again.");
          setShowApprovalModal(false);
          setShowCompletionErrorModal(true);
        }
      } else {
        // Show generic error in modal
        setCompletionErrorMessage(err.message || "Failed to complete order. Please try again.");
        setShowApprovalModal(false);
        setShowCompletionErrorModal(true);
      }
    } finally {
      setApprovalLoading(false);
    }
  };

  // Function to open delete confirmation modal
  const openDeleteModal = () => {
    setShowDeleteModal(true);
  };

  // Function to close delete confirmation modal
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
  };

  // Function to handle order deletion
  const handleDeleteOrder = async () => {
    setDeleteLoading(true);
    try {
      await deleteOrder(order.order_id);

      // Close the modal
      closeDeleteModal();

      // Show success message and redirect to orders page
      setSuccessMessage(
        `Order #${order.order_id} has been deleted successfully.`
      );

      // Redirect to orders page after a brief delay
      setTimeout(() => {
        navigate("/orders");
      }, 1500);
    } catch (err) {
      console.error("Error deleting order:", err);
      setError(`Failed to delete order: ${err.message || "Unknown error"}`);
      setDeleteLoading(false);
    }
  };

  // Function to close response modal
  const closeResponseModal = () => {
    setShowResponseModal(false);
    setPharmacyResponse(null);
  };

  // Helper function to get status text
  const getStatusText = (status) => {
    switch (status) {
      case "to_be_posted":
        return "To Be Posted";
      case "in_progress":
        return "In Progress";
      case "approved":
        return "Approved";
      case "failed":
        return "Failed";
      case "completed":
        return "Completed";
      default:
        return (
          status.charAt(0).toUpperCase() + status.slice(1).replace("_", " ")
        );
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const paginatedItems =
    order?.order_items?.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    ) || [];

  const totalPages = Math.ceil(
    (order?.order_items?.length || 0) / itemsPerPage
  );

  const renderPageContent = () => {
    if (loading) {
      return (
        <Card>
          <LoadingMessage>
            <FaSpinner />
            Loading order request details...
          </LoadingMessage>
        </Card>
      );
    }

    if (error) {
      return (
        <Card>
          <ErrorMessage>{error}</ErrorMessage>
        </Card>
      );
    }

    if (!order) {
      return (
        <Card>
          <ErrorMessage>Order request not found.</ErrorMessage>
        </Card>
      );
    }

    return (
      <Card>
        <OrderDetailsGrid>
          <DetailItem>
            <DetailLabel>Request ID</DetailLabel>
            <DetailValue>#{order.order_id}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Date</DetailLabel>
            <DetailValue>
              {formatDateInEasternDT(order.order_date, order.order_time)}
            </DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Time</DetailLabel>
            <DetailValue>
              {formatTimeInEasternTI(order.order_date, order.order_time)}
            </DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Request Type</DetailLabel>
            <DetailValue style={{ textTransform: "capitalize" }}>
              {order.order_type}
            </DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Pharmacy</DetailLabel>
            <DetailValue>
              {order.pharmacy ? order.pharmacy.pharmacy_name : "N/A"}
            </DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Status</DetailLabel>
            <DetailValue>
              <OrderStatus status={order.status}>
                {getStatusText(order.status)}
              </OrderStatus>
            </DetailValue>
          </DetailItem>
        </OrderDetailsGrid>

        {order.patient_name && (
          <>
            <SectionTitle>Patient Information</SectionTitle>
            <OrderDetailsGrid>
              <DetailItem>
                <DetailLabel>Patient Name</DetailLabel>
                <DetailValue>{order.patient_name}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Email</DetailLabel>
                <DetailValue>{order.patient_email || "N/A"}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Phone</DetailLabel>
                <DetailValue>{order.patient_phone || "N/A"}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Patient ID</DetailLabel>
                <DetailValue>{order.patient_id || "N/A"}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Date of Birth</DetailLabel>
                <DetailValue>{order.patient_dob || order.patient_date_of_birth || "N/A"}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Gender</DetailLabel>
                <DetailValue>{order.patient_gender || "N/A"}</DetailValue>
              </DetailItem>
            </OrderDetailsGrid>
          </>
        )}

        {order.shipping_address && (
          <AddressCard>
            <SectionTitle>Shipping Address</SectionTitle>
            <AddressLine>{order.shipping_address.address1}</AddressLine>
            {order.shipping_address.address2 && (
              <AddressLine>{order.shipping_address.address2}</AddressLine>
            )}
            <CityStateZip>
              {order.shipping_address.city}, {order.shipping_address.state}{" "}
              {order.shipping_address.zipcode}
            </CityStateZip>
          </AddressCard>
        )}

        {order.provider_info && (
          <AddressCard>
            <SectionTitle>Provider Information</SectionTitle>
            <OrderDetailsGrid>
              <DetailItem>
                <DetailLabel>Provider</DetailLabel>
                <DetailValue>
                  {order.provider_info.name ||
                    order.provider_info.display_name ||
                    `${order.provider_info.first_name || ""} ${
                      order.provider_info.last_name || ""
                    }`.trim()}
                </DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Credential</DetailLabel>
                <DetailValue>{order.provider_info.credential || "N/A"}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>NPI</DetailLabel>
                <DetailValue>{order.provider_info.npi || "N/A"}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Practitioner Identifier</DetailLabel>
                <DetailValue>{order.provider_info.practitioner_identifier || "N/A"}</DetailValue>
              </DetailItem>
              <DetailItem>
                <DetailLabel>Licensed States</DetailLabel>
                <DetailValue>
                  {(order.provider_info.licensed_states &&
                    Array.isArray(order.provider_info.licensed_states) &&
                    order.provider_info.licensed_states.length > 0 &&
                    order.provider_info.licensed_states.join(", ")) ||
                    "N/A"}
                </DetailValue>
              </DetailItem>
            </OrderDetailsGrid>
          </AddressCard>
        )}

        <OrderItemsContainer>
          <OrderItemsHeader>
            <SectionTitle
              style={{
                marginBottom: 0,
                paddingBottom: 0,
                borderBottom: "none"
              }}
            >
              Order Items
            </SectionTitle>
          </OrderItemsHeader>

          <OrderItemsList>
            {paginatedItems.length > 0 ? (
              paginatedItems.map((item, index) => (
                <OrderItemCard key={item.order_item_id || index}>
                  <OrderItemDetails>
                    <OrderItemName>
                      {item.medicine?.medicine_name || "Unknown Medicine"}
                    </OrderItemName>
                    <OrderItemVariant>
                      {item.variant
                        ? `${item.variant.quantity} ${item.variant.unit}`
                        : "Standard Package"}
                      {item.variant?.note && ` - ${item.variant.note}`}
                      {item.quantity && ` • Qty: ${item.quantity}`}
                    </OrderItemVariant>
                  </OrderItemDetails>

                  <OrderItemPrice>
                    {formatCurrency(item.price || 0)}
                  </OrderItemPrice>

                  <OrderItemDetails>
                    {item.dosage_instructions &&
                      item.dosage_instructions.length > 0 && (
                        <OrderItemDosage>
                          {item.dosage_instructions.map((instruction, idx) => (
                            <div key={instruction.dosage_instruction_id || idx}>
                              • {instruction.instruction}
                            </div>
                          ))}
                        </OrderItemDosage>
                      )}
                  </OrderItemDetails>
                </OrderItemCard>
              ))
            ) : (
              <div
                style={{
                  padding: "2rem 1.5rem",
                  color: "var(--text-secondary, #aaa)",
                  textAlign: "center"
                }}
              >
                No items in this order
              </div>
            )}
          </OrderItemsList>

          {order?.order_items?.length > itemsPerPage && (
            <PaginationContainer>
              <PageButton
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <FaChevronLeft />
                Previous
              </PageButton>
              <PageInfo>
                Page {currentPage} of {totalPages}
              </PageInfo>
              <PageButton
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                iconRight
              >
                Next
                <FaChevronRight />
              </PageButton>
            </PaginationContainer>
          )}
        </OrderItemsContainer>

        <OrderSummary>
          <OrderSummaryHeader>Order Summary</OrderSummaryHeader>
          <SummaryTable>
            <SummaryRow>
              <SummaryLabel>Subtotal:</SummaryLabel>
              <SummaryValue>
                {formatCurrency(
                  order.order_items && order.order_items.length > 0
                    ? order.order_items.reduce(
                        (sum, item) => sum + (parseFloat(item.price) || 0),
                        0
                      )
                    : 0
                )}
              </SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>Shipping Fee:</SummaryLabel>
              <SummaryValue>
                {formatCurrency(order.shipping_fee || 0)}
              </SummaryValue>
            </SummaryRow>
            <SummaryRow>
              <SummaryLabel>Discount:</SummaryLabel>
              <SummaryValue>
                -{formatCurrency(order.discount || 0)}
              </SummaryValue>
            </SummaryRow>
            <SummaryRow $total>
              <SummaryLabel>Total:</SummaryLabel>
              <SummaryValue $total>
                {formatCurrency(order.total_fee || 0)}
              </SummaryValue>
            </SummaryRow>
          </SummaryTable>
        </OrderSummary>

        <OrderDetailsGrid>
          <DetailItem>
            <DetailLabel>Shipping Fee</DetailLabel>
            <DetailValue>{formatCurrency(order.shipping_fee || 0)}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Discount</DetailLabel>
            <DetailValue>-{formatCurrency(order.discount || 0)}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Filled By</DetailLabel>
            <DetailValue>{order.filled_by || "N/A"}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Created At</DetailLabel>
            <DetailValue>
              {order.created_at
                ? formatDateInEasternDT(order.created_at, "") + " " + formatTimeInEasternTI(order.created_at, "")
                : "N/A"}
            </DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Last Updated</DetailLabel>
            <DetailValue>
              {order.updated_at
                ? formatDateInEasternDT(order.updated_at, "") + " " + formatTimeInEasternTI(order.updated_at, "")
                : "N/A"}
            </DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>Pharmacy</DetailLabel>
            <DetailValue>{order.pharmacy ? order.pharmacy.pharmacy_name : "N/A"}</DetailValue>
          </DetailItem>
        </OrderDetailsGrid>

        {order.notes && (
          <NotesSection>
            <SectionTitle>Notes</SectionTitle>
            <p>{order.notes}</p>
          </NotesSection>
        )}

        {/* Action buttons */}
        <ActionButtons>
          <PrimaryButton
            onClick={handleNavigateToEditPage}
            disabled={order.status === "completed"}
          >
            <FaEdit />
            Edit Order
          </PrimaryButton>

          {/* Status change buttons based on current status */}
          {order.status === "to_be_posted" && user?.is_superuser && (
            <>
              {/* <SuccessButton onClick={handleApproveOrder}>
                <FaCheck />
                Mark as Approved
              </SuccessButton> */}
              {/* <DangerButton onClick={handleRejectOrder}>
                <FaTimes />
                Mark as Failed
              </DangerButton> */}
            </>
          )}

          {/* Complete Order button - only for admin and for approved orders */}
          {order.status === "approved" && user?.is_superuser && (
            <ApproveButton onClick={openApprovalModal}>
              <FaCheckCircle />
              Complete Order
            </ApproveButton>
          )}

          {/* Delete button - only for admin */}
          {user?.is_superuser && (
            <DangerButton onClick={openDeleteModal}>
              <FaTrash />
              Delete Order
            </DangerButton>
          )}
        </ActionButtons>
      </Card>
    );
  };

  return (
    <PageWrapper>
      <OrderContainer>
        <OrderHeader>
          <OrderTitle>Order #{order?.order_id || id} Details</OrderTitle>
          <BackButton to="/orders">
            <FaArrowLeft />
            Back to Orders
          </BackButton>
        </OrderHeader>

        {/* Success Message */}
        {successMessage && (
          <SuccessMessage>
            <FaCheckCircle />
            {successMessage}
          </SuccessMessage>
        )}

        {renderPageContent()}

        {/* Status Change Confirmation Modal */}
        {showModal && (
          <ModalOverlay>
            <ModalContent>
              <ModalHeader>
                <ModalTitle>
                  {modalAction === "approve"
                    ? "Mark Order as Approved"
                    : "Mark Order as Failed"}
                </ModalTitle>
              </ModalHeader>
              <ModalBody>
                <p>
                  {modalAction === "approve"
                    ? "Are you sure you want to mark this order as approved? This action cannot be undone."
                    : "Are you sure you want to mark this order as failed? This action cannot be undone."}
                </p>
                {modalError && (
                  <div
                    style={{
                      color: "var(--error, #ef4444)",
                      marginTop: "1rem",
                      fontSize: "0.9rem",
                      fontWeight: "500"
                    }}
                  >
                    {modalError}
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <CancelButton
                  onClick={handleCloseModal}
                  disabled={updateLoading}
                >
                  Cancel
                </CancelButton>
                {modalAction === "approve" ? (
                  <SuccessButton
                    onClick={handleConfirmAction}
                    disabled={updateLoading}
                  >
                    {updateLoading ? (
                      <>
                        <FaSpinner />
                        Processing...
                      </>
                    ) : (
                      <>
                        <FaCheck />
                        Mark as Approved
                      </>
                    )}
                  </SuccessButton>
                ) : (
                  <DangerButton
                    onClick={handleConfirmAction}
                    disabled={updateLoading}
                  >
                    {updateLoading ? (
                      <>
                        <FaSpinner />
                        Processing...
                      </>
                    ) : (
                      <>
                        <FaTimes />
                        Mark as Failed
                      </>
                    )}
                  </DangerButton>
                )}
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}

        {/* Complete Order Modal */}
        {showApprovalModal && (
          <ModalOverlay>
            <ModalContent>
              <ModalHeader>
                <ModalTitle>Complete Order</ModalTitle>
              </ModalHeader>
              <ModalBody>
                <p>
                  Are you sure you want to complete order #{order?.order_id}?
                </p>
                <p>
                  This action will submit the order to the pharmacy and cannot
                  be undone.
                </p>
              </ModalBody>
              <ModalFooter>
                <CancelButton
                  onClick={closeApprovalModal}
                  disabled={approvalLoading}
                >
                  Cancel
                </CancelButton>
                <ApproveButton
                  onClick={handleCompleteOrder}
                  disabled={approvalLoading}
                >
                  {approvalLoading ? (
                    <>
                      <FaSpinner className="fa-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <FaCheck />
                      Yes, Complete Order
                    </>
                  )}
                </ApproveButton>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <ModalOverlay>
            <ModalContent>
              <ModalHeader>
                <ModalTitle>Confirm Order Deletion</ModalTitle>
              </ModalHeader>
              <ModalBody>
                <p>Are you sure you want to delete order #{order?.order_id}?</p>
                <p
                  style={{ color: "var(--error, #ef4444)", fontWeight: "bold" }}
                >
                  This action cannot be undone.
                </p>
                {order?.status === "completed" && (
                  <p
                    style={{
                      color: "var(--warning, #eab308)",
                      marginTop: "10px"
                    }}
                  >
                    Warning: This order has been completed. Deleting it may
                    affect reporting and record-keeping.
                  </p>
                )}
              </ModalBody>
              <ModalFooter>
                <CancelButton
                  onClick={closeDeleteModal}
                  disabled={deleteLoading}
                >
                  Cancel
                </CancelButton>
                <DangerButton
                  onClick={handleDeleteOrder}
                  disabled={deleteLoading}
                >
                  {deleteLoading ? (
                    <>
                      <FaSpinner className="fa-spin" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <FaTrash />
                      Yes, Delete Order
                    </>
                  )}
                </DangerButton>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}

        {/* Pharmacy Response Modal */}
        {showResponseModal && pharmacyResponse && (
          <ModalOverlay>
            <ModalContent>
              <ModalHeader>
                <ModalTitle>Pharmacy Order Status</ModalTitle>
              </ModalHeader>
              <ModalBody>
                <PharmacyResponseContainer>
                  <PharmacyResponseHeader>
                    <ResponseStatus success={pharmacyResponse.overall_success}>
                      {pharmacyResponse.overall_success ? (
                        <>
                          <FaCheckCircle />
                          Order has been placed successfully on{" "}
                          {pharmacyResponse.pharmacy_name}
                        </>
                      ) : (
                        <>
                          <FaTimesCircle />
                          Order placing error on{" "}
                          {pharmacyResponse.pharmacy_name}
                        </>
                      )}
                    </ResponseStatus>
                  </PharmacyResponseHeader>

                  <p>Total medications: {pharmacyResponse.medication_count}</p>

                  <MedicationList>
                    {pharmacyResponse.medications.map((med, index) => (
                      <MedicationItem key={index}>
                        <StatusIcon success={med.success}>
                          {med.success ? <FaCheckCircle /> : <FaTimesCircle />}
                        </StatusIcon>
                        <span>{med.medicine_name}</span>
                      </MedicationItem>
                    ))}
                  </MedicationList>

                  {pharmacyResponse.info && (
                    <div style={{ marginTop: "1rem" }}>
                      <p>
                        <strong>Additional Information:</strong>
                      </p>
                      <p>{pharmacyResponse.info}</p>
                    </div>
                  )}

                  {pharmacyResponse.errors &&
                    pharmacyResponse.errors.length > 0 && (
                      <div style={{ marginTop: "1rem" }}>
                        <p>
                          <strong>Errors:</strong>
                        </p>
                        <ErrorList>
                          {pharmacyResponse.errors.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ErrorList>
                      </div>
                    )}
                </PharmacyResponseContainer>
              </ModalBody>
              <ModalFooter>
                <Button onClick={closeResponseModal}>Close</Button>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}

        {/* Completion Error Modal */}
        {showCompletionErrorModal && (
          <ModalOverlay>
            <ModalContent>
              <ModalHeader>
                <ModalTitle>Order Completion Error</ModalTitle>
              </ModalHeader>
              <ModalBody>
                <p style={{ color: "var(--error)", marginBottom: "1rem" }}>
                  {completionErrorMessage}
                </p>
                <p>
                  The order status has been updated. The page will reflect the current status.
                </p>
              </ModalBody>
              <ModalFooter>
                <Button onClick={closeCompletionErrorModal}>
                  Close
                </Button>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}
      </OrderContainer>
    </PageWrapper>
  );
}

export default OrderDetailPage;
